import type { ToggleName } from '@tilelist/domain/experiments/Toggles.ts';

declare global {
    namespace App {
        interface Onex {
            DUMMY: 'groupA' | 'groupB';
            DUMMY_N_GROUP: 'groupA' | 'groupB' | 'groupC';
            E3891: 'E3891A' | 'E3891B';
            E3772: 'StatusQuo' | 'Testgroup';
            E3822: 'E3822A' | 'E3822B';
            E3804: 'E3804A' | 'E3804B';
            E3918: 'E3918A' | 'E3918B';
            E3917: 'E3917A' | 'E3917B';
            E3763: 'StatusQuo' | 'Positions';
            E3886: 'E3886A' | 'E3886B';
            E3909: 'E3909A' | 'E3909B';
            E3803: 'E3803A' | 'E3803B' | 'E3803C';
            E3892: 'E3892A' | 'E3892B' | 'E3892C';
            E3738: 'E3738A' | 'E3738B';
        }
    }
}

type OnexExperimentId = keyof App.Onex;
type OnexGroup<Id extends OnexExperimentId> = App.Onex[Id];
export type OnexAssignments = Partial<{ [K in OnexExperimentId]: App.Onex[K] }>;

/**
 *
 */
export const EXPERIMENT_ONEX_IDS = {
    DUMMY: 'DUMMY',
    DUMMY_N_GROUP: 'DUMMY_N_GROUP',
    ADDITIONAL_IMAGES: 'E3891',
    TEASER_VARIATION_BOOST: 'E3772',
    GALLERY_LANDSCAPE_TWO_COLUMN: 'E3822',
    BRAND_TILE: 'E3804',
    ARRANGEMENT_LIST_ELEMENTS_ON_TOP: 'E3763',
    CLEAN_TILE_V3: 'E3918',
    DELIVERY_DATE: 'E3917',
    SPA_VIA_SEARCH: 'E3886',
    CONSISTENT_IMAGES: 'E3909',
    WISHLIST_ELEMENT: 'E3803',
    PRICING_COMPONENT: 'E3892',
    LOAD_MORE_TOPICPAGE: 'E3738',
} as const satisfies Record<string, OnexExperimentId>;

export type ExperimentId = keyof typeof EXPERIMENT_ONEX_IDS;

export type ExperimentOnexId = (typeof EXPERIMENT_ONEX_IDS)[ExperimentId];

export type ExperimentGroups = {
    [K in ExperimentId]: OnexGroup<(typeof EXPERIMENT_ONEX_IDS)[K]>;
};

/**
 *
 */
export interface ExperimentConfig<E extends keyof ExperimentGroups> {
    /**
 *
 */
    onexExperimentId: (typeof EXPERIMENT_ONEX_IDS)[E];

    /**
 *
 *
 *
 */
    experimentToggle: ToggleName;

    /**
 *
 *
 *
 *
 */
    featureToggle: ToggleName;

    /**
 *
 *
 */
    groups: ReadonlyArray<ExperimentGroups[E]>;

    /**
 *
 *
 */
    defaultGroup: ExperimentGroups[E];

    /**
 *
 *
 */
    featureOnGroup: ExperimentGroups[E];
}

export type ExperimentConfigs = {
    [K in keyof ExperimentGroups]: ExperimentConfig<K>;
};

/**
 *
 *
 *
 *
 *
 *
 *
 */
export const EXPERIMENTS = {
    DUMMY: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.DUMMY,
        experimentToggle: 'EXPERIMENT_DUMMY_ACTIVE',
        featureToggle: 'FEATURE_DUMMY_USE_GROUP_A',
        groups: ['groupA', 'groupB'],
        defaultGroup: 'groupB',
        featureOnGroup: 'groupA',
    },
    DUMMY_N_GROUP: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.DUMMY_N_GROUP,
        experimentToggle: 'EXPERIMENT_DUMMY_N_GROUP_ACTIVE',
        featureToggle: 'FEATURE_DUMMY_N_GROUP_USE_GROUP_B',
        groups: ['groupA', 'groupB', 'groupC'],
        defaultGroup: 'groupA',
        featureOnGroup: 'groupB',
    },
    ADDITIONAL_IMAGES: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.ADDITIONAL_IMAGES,
        experimentToggle: 'REPTILE_1522_ADDITIONAL_IMAGES_EXPERIMENT',
        featureToggle: 'REPTILE_1522_ADDITIONAL_IMAGES',
        groups: ['E3891A', 'E3891B'],
        defaultGroup: 'E3891A',
        featureOnGroup: 'E3891B',
    },
    TEASER_VARIATION_BOOST: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.TEASER_VARIATION_BOOST,
        experimentToggle: 'REPTILE_1564_BOOST_TEASER_VARIATIONS_EXPERIMENT',
        featureToggle: 'REPTILE_1564_BOOST_TEASER_VARIATIONS',
        groups: ['StatusQuo', 'Testgroup'],
        defaultGroup: 'StatusQuo',
        featureOnGroup: 'Testgroup',
    },
    GALLERY_LANDSCAPE_TWO_COLUMN: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.GALLERY_LANDSCAPE_TWO_COLUMN,
        experimentToggle: 'REPTILE_1590_TWO_COLUMN_LANDSCAPE_EXPERIMENT',
        featureToggle: 'REPTILE_1590_TWO_COLUMN_LANDSCAPE',
        groups: ['E3822A', 'E3822B'],
        defaultGroup: 'E3822A',
        featureOnGroup: 'E3822B',
    },
    BRAND_TILE: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.BRAND_TILE,
        experimentToggle: 'REPTILE_1537_BRAND_TILE_EXPERIMENT',
        featureToggle: 'REPTILE_1537_BRAND_TILE',
        groups: ['E3804A', 'E3804B'],
        defaultGroup: 'E3804A',
        featureOnGroup: 'E3804B',
    },
    ARRANGEMENT_LIST_ELEMENTS_ON_TOP: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.ARRANGEMENT_LIST_ELEMENTS_ON_TOP,
        experimentToggle: 'REPTILE_1288_ARRANGEMENT_EXPERIMENT',
        featureToggle: 'REPTILE_1288_ARRANGEMENT',
        groups: ['StatusQuo', 'Positions'],
        defaultGroup: 'StatusQuo',
        featureOnGroup: 'Positions',
    },
    CLEAN_TILE_V3: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.CLEAN_TILE_V3,
        experimentToggle: 'REPTILE_1689_CLEAN_TILE_V3_EXPERIMENT',
        featureToggle: 'REPTILE_1689_CLEAN_TILE_V3',
        groups: ['E3918A', 'E3918B'],
        defaultGroup: 'E3918A',
        featureOnGroup: 'E3918B',
    },
    DELIVERY_DATE: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.DELIVERY_DATE,
        experimentToggle: 'REPTILE_1639_DELIVERY_DATE_EXPERIMENT',
        featureToggle: 'REPTILE_1639_DELIVERY_DATE',
        groups: ['E3917A', 'E3917B'],
        defaultGroup: 'E3917A',
        featureOnGroup: 'E3917B',
    },
    SPA_VIA_SEARCH: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.SPA_VIA_SEARCH,
        experimentToggle: 'REPTILE_1628_SPA_VIA_SEARCH_EXPERIMENT',
        featureToggle: 'REPTILE_1628_SPA_VIA_SEARCH',
        groups: ['E3886A', 'E3886B'],
        defaultGroup: 'E3886A',
        featureOnGroup: 'E3886B',
    },
    CONSISTENT_IMAGES: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.CONSISTENT_IMAGES,
        experimentToggle: 'REPTILE_1672_CONSISTENT_IMAGES_EXPERIMENT',
        featureToggle: 'REPTILE_1672_CONSISTENT_IMAGES',
        groups: ['E3909A', 'E3909B'],
        defaultGroup: 'E3909A',
        featureOnGroup: 'E3909B',
    },
    WISHLIST_ELEMENT: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.WISHLIST_ELEMENT,
        experimentToggle: 'REPTILE_1663_WISHLIST_ELEMENT_EXPERIMENT',
        featureToggle: 'REPTILE_1663_WISHLIST_ELEMENT',
        groups: ['E3803A', 'E3803B', 'E3803C'],
        defaultGroup: 'E3803A',
        featureOnGroup: 'E3803B',
    },
    PRICING_COMPONENT: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.PRICING_COMPONENT,
        experimentToggle: 'REPTILE_1643_PRICING_COMPONENT_EXPERIMENT',
        featureToggle: 'REPTILE_1643_PRICING_COMPONENT',
        groups: ['E3892A', 'E3892B', 'E3892C'],
        defaultGroup: 'E3892A',
        featureOnGroup: 'E3892B',
    },
    LOAD_MORE_TOPICPAGE: {
        onexExperimentId: EXPERIMENT_ONEX_IDS.LOAD_MORE_TOPICPAGE,
        experimentToggle: 'REPTILE_1492_LOAD_MORE_TOPICPAGE_EXPERIMENT',
        featureToggle: 'REPTILE_1492_LOAD_MORE_TOPICPAGE',
        groups: ['E3738A', 'E3738B'],
        defaultGroup: 'E3738A',
        featureOnGroup: 'E3738B',
    },
} as const satisfies ExperimentConfigs;
